import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Card, Row, Col, InputGroup, InputGroupText, Input, Button, Spinner } from "reactstrap";
import { AutoSizer, Table, Column, SortDirection } from "react-virtualized";
import { BsBarChartFill, BsSearch } from "react-icons/bs";
import PageHeader from "../../sharedComponents/Header/PageHeader";
import MobileMenuSlideDrawer from "../../sharedComponents/SlideDrawer/MobileMenuSlideDrawer";
import { Theme } from "../../config/constants";
import { IoMdInformationCircle } from "react-icons/io";
import { getLast6Months } from "../../utils/dateUtils";
import datasuiteApi from "../../api/datasuiteApi";
import { toast } from "react-toastify";
import EmptyDataMessage from "../../sharedComponents/ErrorMessages/EmptyDataMessage";
import formatters from "../../config/formatters";
import { sortableHeaderRenderer } from "../../sharedComponents/Tables/SortableHeaderRenderer";
import { Tooltip } from "@mui/material";
import moment from "moment";
import { downloadCsvFile } from "../../config/dataManipulations";
import useManufacturerData from "../../sharedComponents/useManufacturerData/useManufacturerData";
import ManufacturerSelect from "../../sharedComponents/ManufacturerSelect/ManufacturerSelect";
import FiltersWidget from "../../sharedComponents/Filters/FiltersWidget";
import dashboardApi from "../../api/dashboardApi";
import { useQuery } from "react-query";
import { showModal } from "../../reducers/ModalsReducer/ModalsActions";
import ShareOpportunityModal from "./Modals/ShareOpportunityModal";

const MatchOpportunityIdentifier = () => {
    const dispatch = useDispatch();
    const { numberFormatter } = formatters();
    const chainData = useSelector(({user}) => user.chainData);
    const emergingData = useSelector(({user}) => user.emergingData);
    const infoString = "Our Match Opportunity Identifier algorithm compares your products to competitors using Category, Name, and Pack Size. If no match is found, you’ll see it as an Opportunity in “Growth Opportunities” but not the item it matches.";
    const tableString = "These are matched items for the selected category.";
    const [startDate, endDate] = getLast6Months();
    const [inputText, setInputText] = useState('');
    const [prechosenFilters, setPrechosenFilters] = useState({});
    const [selectedFilters, setSelectedFilters] = useState(null);
    const [itemMatchData, setItemMatchData] = useState(null);
    const [dataLoading, setDataLoading] = useState(true);
    const [sortBy, setSortBy] = useState('opportunity_cases');
    const [sortDirection, setSortDirection] = useState(SortDirection.DESC);
    const { manufacturerParams } = useManufacturerData();
    const dateRanges = { 
        'Last Week': [moment().subtract(1, 'weeks'), moment()],
        'Last 2 Weeks': [moment().subtract(2, 'weeks'), moment()],
        'Last Month': [moment().subtract(1, 'months'), moment()],
        'Last 3 Months': [moment().subtract(3, 'months'), moment()],
        'Last 6 Months': [moment().subtract(6, 'months'), moment()]
    };

    const useGetCategoryFilters = () => useQuery({
        queryKey: ['categoryFilters'],
        queryFn: () => dashboardApi.topCategories.post({
            "chainData": chainData,
            "emergingData": emergingData,
            "startDate": startDate,
            "endDate": endDate,
            ...manufacturerParams
        }).then((response) => {
            return response.data.map(c => c.category);
        }).catch(err => {
            console.log(err);
        }),
        enabled: chainData || emergingData
    });
    const { refetch: refetchCategoryFilters } = useGetCategoryFilters();

    const sortedItemMatchData = (() => {
        if (!itemMatchData) return [];
        const sorted = [...itemMatchData];
        switch (sortBy) {
            case 'location_name' : {
                sorted.sort((a, b) => {
                    if (sortDirection === SortDirection.ASC) {
                        return (a.location_name.localeCompare(b.location_name));
                    } else {
                        return (b.location_name.localeCompare(a.location_name));
                    }
                });
                break;
            }
            case 'match_item_description' : {
                sorted.sort((a, b) => {
                    if (sortDirection === SortDirection.ASC) {
                        return (a.match_item_description.localeCompare(b.match_item_description));
                    } else {
                        return (b.match_item_description.localeCompare(a.match_item_description));
                    }
                });
                break;
            }
            case 'category' : {
                sorted.sort((a, b) => {
                    if (sortDirection === SortDirection.ASC) {
                        return (a.category.localeCompare(b.category));
                    } else {
                        return (b.category.localeCompare(a.category));
                    }
                });
                break;
            }
            case 'your_cases' : {
                sorted.sort((a, b) => {
                    if (sortDirection === SortDirection.ASC) {
                        return (a.your_cases) - (b.your_cases);
                    } else {
                        return (b.your_cases) - (a.your_cases);
                    }
                });
                break;
            }
            case 'opportunity_cases' : {
                sorted.sort((a, b) => {
                    if (sortDirection === SortDirection.ASC) {
                        return (a.opportunity_cases) - (b.opportunity_cases);
                    } else {
                        return (b.opportunity_cases) - (a.opportunity_cases);
                    }
                });
                break;
            }
            case 'total_cases' : {
                sorted.sort((a, b) => {
                    if (sortDirection === SortDirection.ASC) {
                        return (a.total_cases) - (b.total_cases);
                    } else {
                        return (b.total_cases) - (a.total_cases);
                    }
                });
                break;
            }
            default: {
                // Do nothing
            }
        }
        return sorted;
    })();

    const searchedItemMatchData = (() => {
        if (sortedItemMatchData?.length <= 0 || inputText === null || inputText === '') return [];
        const initialData = [...sortedItemMatchData];
        const inputTextLowercase = inputText.toLowerCase()
        const searched = initialData.filter((rowData) => {
            return rowData.category.toLowerCase().includes(inputTextLowercase)
            || rowData.match_item_description.toLowerCase().includes(inputTextLowercase)
            || rowData.location_name.toLowerCase().includes(inputTextLowercase);
        });
        return searched;
    })();

    const getMatchOpportunityItems = (selectedFilters = []) => {
        const params = {
            startDate: selectedFilters?.startDate,
            endDate: selectedFilters?.endDate,
            chainData: selectedFilters?.chainData,
            emergingData: selectedFilters?.emergingData,
            categories: selectedFilters?.categories,
            distributors: selectedFilters?.distributors,
            locations: selectedFilters?.locations,
            segments: selectedFilters?.segments,
            countries: selectedFilters?.countries,
            states: selectedFilters?.states,
            ...manufacturerParams
        };
        datasuiteApi.matchOpportunityIdentifier.post(params).then((response) => {
            setItemMatchData(response.data);
            setDataLoading(false);
        }).catch((err) => {
            console.log(err);
            toast.error(err.message);
            setDataLoading(false);
        });
    };

    const onClickSearch = (selectedFilters = []) => {
        setDataLoading(true);
        setSelectedFilters(selectedFilters);
        getMatchOpportunityItems(selectedFilters);
    };

    const onClickExport = () => {
        const filename = "BEP_Match_Opportunities_" + moment().format('YYYYMMDD_hhmmss').toString();
        downloadCsvFile(sortedItemMatchData, filename);
        toast.success('Data successfully exported to downloads folder.')
    };

    const onClickShareReport = () => {
        dispatch(showModal(ShareOpportunityModal, {selectedFilters: selectedFilters ?? prechosenFilters}));
    };

    useEffect(() => {
        const fetchCategories = async () => {
            const categoryFilters = await refetchCategoryFilters().then((res) => {
                return res.data;
            }).catch(err => { return []; });
            if (categoryFilters.length > 0) {
                categoryFilters.sort();
                setPrechosenFilters({
                    categories: [categoryFilters[0]]
                });
                getMatchOpportunityItems({
                    startDate,
                    endDate,
                    chainData,
                    emergingData,
                    categories: [categoryFilters[0]]
                });
            } else {
                setDataLoading(false);
            }
        };
        fetchCategories();
        // eslint-disable-next-line
    }, []);

    return (
        <>
            <PageHeader title='SalesStream' subtitle='Match Opportunity Identifier' icon={<BsBarChartFill fill='url(#icon-gradient)'/>}/>
            <div className='bg-transparent p-3 matchOpportunityIdentifierContainer'>
                <Row className='label-text text-size-14 d-flex align-items-center justify-content-end mx-auto pb-2'>
                    <Col className="pe-0">
                        <ManufacturerSelect />
                    </Col>
                </Row>
                <Card style={{minHeight: '56px', background: Theme().colors.accent}}>
                    <Col className="w-100">
                        <Row className="w-100 h-100 m-auto" style={{alignItems: 'center'}}>
                            <Col className="w-fc">
                                <IoMdInformationCircle style={{fontSize: '20px', color: 'white'}} />
                            </Col>
                            <Col style={{fontWeight: '600', color: 'white'}}>
                                {infoString}
                            </Col>
                        </Row>
                    </Col>
                </Card>
                <FiltersWidget
                    minDate={startDate}
                    maxDate={endDate}
                    dateRanges={dateRanges}
                    fromDate={startDate}
                    toDate={endDate}
                    prechosenFilters={selectedFilters ?? prechosenFilters}
                    saveFilterSetsEnabled={false}
                    filterSetOptionsEnabled={false}
                    onApplyFilters={onClickSearch}
                />
                <Card className="px-2 py-3" style={{height: 'calc(100vh - 140px)'}}>
                    <Col style={{display: 'flex', flexDirection: 'column'}}>
                        <Row className="w-100 mx-auto pe-0" style={{paddingBottom: '12px'}}>
                            <Col className="main-header text-size-18 w-fc" style={{alignContent: 'center'}}>
                                <span>Match Opportunities</span>
                            </Col>
                            <Col className="w-fc ps-0" style={{alignContent: 'center'}}>
                                <Tooltip title={tableString} placement="top" arrow>
                                    <div style={{position: 'relative', bottom: '2px'}}>
                                        <IoMdInformationCircle color={Theme().colors.accent} style={{fontSize: "18px"}} />
                                    </div>
                                </Tooltip>
                            </Col>
                            <Col></Col>
                            <Col className="w-fc">
                                <Button
                                    outline
                                    className='py-1 button-label'
                                    size='sm'
                                    style={{width: '80px'}}
                                    disabled={dataLoading}
                                    color={Theme().colors.accentName}
                                    onClick={() => onClickShareReport()}>Share</Button>
                            </Col>
                            <Col className="w-fc">
                                <Button
                                    outline
                                    className='py-1 button-label'
                                    size='sm'
                                    style={{width: '80px'}}
                                    disabled={dataLoading}
                                    color={Theme().colors.accentName}
                                    onClick={onClickExport}>Export</Button>
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <InputGroup>
                                    <InputGroupText>
                                        <BsSearch/>
                                    </InputGroupText>
                                    <Input className='text-input' value={inputText} onChange={(e) => setInputText(e.target.value)} placeholder='Search...'/>
                                </InputGroup>
                            </Col>
                        </Row>
                        <Row style={{paddingTop: '8px', flex: '1'}}>
                            {(itemMatchData?.length <= 0 || itemMatchData == null) && !dataLoading &&
                            <Col className="m-auto">
                                <EmptyDataMessage />
                                <div style={{textAlign: 'center'}}>Try broadening your search (at least one Category is required).</div>
                            </Col>
                            }
                            {dataLoading &&
                            <Col className="m-auto">
                                <div className="mx-auto my-auto" style={{height:"2.5%", width:"2.5%"}}>
                                    <Spinner animation="border" />
                                </div>
                            </Col>
                            }
                            {itemMatchData?.length > 0 && !dataLoading &&
                            <Col>
                                <AutoSizer>
                                    {({width, height}) => (
                                        <Table className="table totalOpportunitiesTable border-grayLight" id="totalOpportunitiesTable"
                                            width={width}
                                            height={height}
                                            headerHeight={34.5}
                                            rowHeight={38}
                                            rowCount={(searchedItemMatchData.length > 0 || inputText !== '') ? searchedItemMatchData?.length : sortedItemMatchData?.length}
                                            rowGetter={({ index }) => (searchedItemMatchData.length > 0 || inputText !== '') ? searchedItemMatchData?.[index] : sortedItemMatchData?.[index]}
                                            sort={({ sortBy: sb, sortDirection: sd }) => {
                                                setSortBy(sb);
                                                setSortDirection(sd);
                                            }}
                                            sortBy={sortBy}
                                            sortDirection={sortDirection}
                                        >
                                            <Column
                                            label='Parent Group'
                                            dataKey='location_name'
                                            width={width * 0.13}
                                            headerRenderer={sortableHeaderRenderer}
                                            />
                                            <Column
                                            label='MIN'
                                            dataKey='match_item_min'
                                            width={width * 0.05}
                                            />
                                            <Column
                                            label='Description'
                                            dataKey='match_item_description'
                                            width={width * 0.15}
                                            headerRenderer={sortableHeaderRenderer}
                                            />
                                            <Column
                                            label='Category'
                                            dataKey='category'
                                            width={width * 0.12}
                                            headerRenderer={sortableHeaderRenderer}
                                            />
                                            <Column
                                            label='Pack Size'
                                            dataKey='match_item_pack'
                                            width={width * 0.1}
                                            />
                                            <Column
                                            label='Your Cases'
                                            dataKey='your_cases'
                                            width={width * 0.1}
                                            cellRenderer={({ cellData }) => <span>{numberFormatter.format(Math.round(cellData))}</span>}
                                            headerRenderer={sortableHeaderRenderer}
                                            />
                                            <Column
                                            label='Opportunity Cases'
                                            dataKey='opportunity_cases'
                                            width={width * 0.15}
                                            cellRenderer={({ cellData }) => <span>{numberFormatter.format(Math.round(cellData))}</span>}
                                            headerRenderer={sortableHeaderRenderer}
                                            />
                                            <Column
                                            label='Total Cases'
                                            dataKey='total_cases'
                                            width={width * 0.1}
                                            cellRenderer={({ cellData }) => <span>{numberFormatter.format(Math.round(cellData))}</span>}
                                            headerRenderer={sortableHeaderRenderer}
                                            />
                                        </Table>
                                    )}
                                    </AutoSizer>
                            </Col>
                            }
                        </Row>
                    </Col>
                </Card>
            </div>
            <MobileMenuSlideDrawer />
        </>

    )
}

export default MatchOpportunityIdentifier;
